import React, { useState } from "react";
import { Swords, Users, Shield, Target, Flame, Play, X, UserPlus, Copy, Check, Radio } from "lucide-react";
import { toast } from "sonner";
import { Avatar, Username } from "../UserBadge";

const MODES = [
  { id: "5v5", name: "5v5 Competitive", desc: "Format oficial MR12, Veto de hărți, ELO & clasament", icon: Shield, playersReq: 10, color: "#FF4F00" },
  { id: "1v1", name: "1v1 Aim Arena", desc: "Dueluri rapide 1v1 pe hărți de aim (Aim Map, Redline)", icon: Target, playersReq: 2, color: "#00F0FF" },
  { id: "2v2", name: "2v2 Wingman", desc: "Meciuri rapide în echipă de 2 pe jumătăți de hartă", icon: Users, playersReq: 4, color: "#a200ff" },
  { id: "retakes", name: "Retakes 3v3", desc: "Scenarii de dezamorsare și apărare a bombei", icon: Flame, playersReq: 6, color: "#ffaa00" },
];

export default function MatchmakingConsole({ user, statusData, onJoinQueue, onLeaveQueue, isSearching }) {
  const [selectedMode, setSelectedMode] = useState("5v5");
  const [region, setRegion] = useState("EU-RO");

  const counts = statusData?.counts || {};
  const currentQueue = statusData?.user_queue;

  const handleQueueClick = () => {
    if (!user) {
      toast.error("Trebuie să fii autentificat pentru a juca în Matchmaking!");
      return;
    }
    if (isSearching) {
      if (onLeaveQueue) onLeaveQueue();
    } else {
      if (onJoinQueue) onJoinQueue(selectedMode, region);
    }
  };

  return (
    <div className="p-6 sm:p-8 rounded-3xl bg-[#080a0f] border border-white/10 shadow-2xl space-y-8">
      {/* Header Info */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <Swords className="w-7 h-7 text-[var(--tf-primary)]" />
            <h2 className="text-2xl font-black uppercase text-white tracking-wider">INDUNGI Matchmaking Console</h2>
          </div>
          <p className="text-xs text-zinc-400 mt-1">Sistem automat de meciuri competitive 1v1, 2v2 și 5v5 cu ELO și servere dedicate CS2.</p>
        </div>

        {/* Region Selector */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-black/60 border border-white/10 text-xs">
          <Radio className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
          <span className="text-zinc-400 font-mono">Regiune:</span>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            disabled={isSearching}
            className="bg-transparent text-white font-bold font-mono focus:outline-none cursor-pointer"
          >
            <option value="EU-RO" className="bg-zinc-900 text-white">🇷🇴 EU-RO (București ~5ms)</option>
            <option value="EU-C" className="bg-zinc-900 text-white">🇪🇺 EU-Central (Frankfurt ~30ms)</option>
          </select>
        </div>
      </div>

      {/* Modes Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {MODES.map((m) => {
          const Icon = m.icon;
          const isSelected = selectedMode === m.id;
          const queueCount = counts[m.id] || 0;
          return (
            <div
              key={m.id}
              onClick={() => !isSearching && setSelectedMode(m.id)}
              className={`p-5 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between relative overflow-hidden ${
                isSelected 
                  ? "bg-white/[0.04] shadow-xl scale-[1.02]" 
                  : "bg-black/40 border-white/5 hover:border-white/15 hover:bg-white/[0.02]"
              }`}
              style={{ borderColor: isSelected ? m.color : undefined }}
            >
              {isSelected && (
                <div 
                  className="absolute top-0 inset-x-0 h-1"
                  style={{ backgroundColor: m.color, boxShadow: `0 0 15px ${m.color}` }}
                />
              )}

              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/5 border border-white/10" style={{ color: m.color }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-white/5 text-zinc-400 border border-white/5">
                    {queueCount} în coadă
                  </span>
                </div>

                <h3 className="text-sm font-black uppercase text-white tracking-wide">{m.name}</h3>
                <p className="text-xs text-zinc-400 mt-1">{m.desc}</p>
              </div>

              <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-[11px] font-mono text-zinc-500">
                <span>{m.playersReq} jucători</span>
                <span className="font-bold text-white uppercase">{isSelected ? "● Selectat" : "Alege"}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* SEARCH ACTION BAR */}
      <div className="p-6 rounded-2xl bg-black/60 border border-white/10 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {user ? (
            <div className="flex items-center gap-3">
              <Avatar user={user} size={44} hover={true} />
              <div>
                <Username user={user} className="font-bold text-sm" />
                <span className="text-xs text-zinc-400 block font-mono">
                  {user.cs2_rank || "Global Elite"} • <strong className="text-orange-400">{user.elo || 1200} ELO</strong>
                </span>
              </div>
            </div>
          ) : (
            <div className="text-xs text-zinc-400">
              Autentifică-te pentru a intra în meciuri oficiale.
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          {isSearching && (
            <div className="flex items-center gap-2 font-mono text-xs text-orange-400">
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-ping" />
              <span>Se caută oponenți ({currentQueue?.mode?.toUpperCase() || selectedMode.toUpperCase()})...</span>
            </div>
          )}

          <button
            onClick={handleQueueClick}
            className={`px-10 py-4 rounded-2xl font-black uppercase tracking-widest text-sm transition-all duration-300 shadow-2xl flex items-center gap-3 ${
              isSearching
                ? "bg-red-500 hover:bg-red-600 text-white shadow-red-500/30 hover:scale-105"
                : "bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-black shadow-orange-500/40 hover:scale-105 active:scale-95"
            }`}
          >
            {isSearching ? (
              <>
                <X className="w-5 h-5" /> ANULEAZĂ CĂUTAREA
              </>
            ) : (
              <>
                <Play className="w-5 h-5 fill-current" /> GĂSEȘTE MECI ({selectedMode.toUpperCase()})
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
